// series-card.tsx
import { Card, Tag, Flex, Typography, Space, Button, Divider } from 'antd';
import React, { FunctionComponent } from 'react';
import {
  AssetOrigin,
  Languages,
  Series as SeriesType,
  SeriesSubject,
  TimezoneLabels,
} from '@monorepo/types';
import { useStore } from '../../helpers/use-store';
import SocialIcon from '../../components/social-provider-icon/social-provider-icon';
import { compact, startCase } from 'lodash';
import { STYLE_MAPPING } from '../ai-image-styles/ai-image-styles';
import {
  EyeOutlined,
  ThunderboltOutlined,
  GlobalOutlined,
  SoundOutlined,
  VideoCameraOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  StopOutlined,
} from '@ant-design/icons';

import { useTranslation } from 'react-i18next';
import './series-card.scss';

const { Text, Title } = Typography;

interface Props {
  series: SeriesType;
  onView: () => void;
}

const getSeriesTypeColor = (type: AssetOrigin) => {
  switch (type) {
    case AssetOrigin.AIImages:
      return '#52c41a';
    case AssetOrigin.StockImages:
      return '#faad14';
    default:
      return '#d9d9d9';
  }
};

export const SeriesCard: FunctionComponent<Props> = ({ series, onView }) => {
  const { t } = useTranslation();
  const {
    dataStore: { voiceoverStore, socialAccountStore },
  } = useStore();

  const voiceOver = voiceoverStore.get(series.voiceoverId);
  const socialAccountIds =
    series.socialAccountIds && series.socialAccountIds.length > 0
      ? series.socialAccountIds
      : compact([series.socialAccountId]);

  const socialAccounts = socialAccountIds
    .map((id) => socialAccountStore.get(id))
    .filter(Boolean);

  const rawCategoryName = startCase(
    Object.keys(SeriesSubject).find(
      (key) => SeriesSubject[key] === series.subjectType
    )
  );
  const categoryName = t(`seriesForm.subject.${series.subjectType}`, {
    defaultValue: rawCategoryName,
  });

  const languageName = Object.keys(Languages).find(
    (key) => Languages[key] === series.language
  );

  const rawVisualStyle = startCase(
    Object.keys(AssetOrigin).find(
      (key) => AssetOrigin[key] === series.assetOrigin
    )
  );
  const visualStyle = t(`assetOrigin.${series.assetOrigin}`, {
    defaultValue: rawVisualStyle,
  });

  const styleEntry = series.assetStyle
    ? STYLE_MAPPING[series.assetStyle as keyof typeof STYLE_MAPPING]
    : null;
  const assetStyleLabel = styleEntry
    ? t(`aiStyles.${styleEntry.nameKey}`, { defaultValue: styleEntry.name })
    : series.assetStyle;

  return (
    <Card className="series-card" hoverable onClick={onView}>
      {/* Header */}
      <div className="series-card-header">
        <Flex justify="space-between" align="flex-start">
          <div className="series-card-title-section">
            <Title level={4} style={{ margin: 0, marginBottom: '4px' }}>
              {series.name}
            </Title>
            <Text type="secondary" style={{ fontSize: '13px' }}>
              {categoryName}
            </Text>
          </div>
          <Tag
            icon={series.isActive ? <CheckCircleOutlined /> : <StopOutlined />}
            color={series.isActive ? 'success' : 'default'}
          >
            {series.isActive
              ? t('series.card.active')
              : t('series.card.disabled')}
          </Tag>
        </Flex>
      </div>

      <Divider style={{ margin: '16px 0' }} />

      {/* Main Info Grid */}
      <div className="series-card-grid">
        {/* Language & Voice */}
        <div className="series-info-item">
          <GlobalOutlined className="info-icon" />
          <div>
            <Text type="secondary" className="info-label">
              {t('series.card.language')}
            </Text>
            <Text strong className="info-value">
              {languageName}
            </Text>
          </div>
        </div>

        <div className="series-info-item">
          <SoundOutlined className="info-icon" />
          <div>
            <Text type="secondary" className="info-label">
              {t('series.card.voice')}
            </Text>
            <Text strong className="info-value">
              {startCase(voiceOver?.name)}
            </Text>
          </div>
        </div>

        {/* Duration & Frequency */}
        <div className="series-info-item">
          <VideoCameraOutlined className="info-icon" />
          <div>
            <Text type="secondary" className="info-label">
              {t('series.card.duration')}
            </Text>
            <Text strong className="info-value">
              {series.duration}s
            </Text>
          </div>
        </div>

        <div className="series-info-item">
          <CalendarOutlined className="info-icon" />
          <div>
            <Text type="secondary" className="info-label">
              {t('series.card.frequency')}
            </Text>
            <Text strong className="info-value">
              {series.videosPerDay || 1}/day
            </Text>
          </div>
        </div>
      </div>

      {/* Visual Style */}
      <div className="series-visual-style">
        <Flex align="center" gap={8}>
          <ThunderboltOutlined
            style={{ color: getSeriesTypeColor(series.assetOrigin) }}
          />
          <Tag
            color={getSeriesTypeColor(series.assetOrigin)}
            style={{ margin: 0 }}
          >
            {visualStyle}
          </Tag>
          {series.assetStyle && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {assetStyleLabel}
            </Text>
          )}
        </Flex>
      </div>

      {/* Social Accounts */}
      {socialAccounts.length > 0 && (
        <>
          <Divider style={{ margin: '12px 0' }} />
          <div className="series-social-accounts">
            <Text
              type="secondary"
              style={{
                fontSize: '12px',
                marginBottom: '8px',
                display: 'block',
              }}
            >
              {t('series.card.publishingTo')}
            </Text>
            <Flex gap={8} wrap="wrap">
              {socialAccounts.map((account, index) => {
                if (!account) {
                  return null;
                }

                return (
                  <Tag
                    key={index}
                    icon={<SocialIcon provider={account.provider} />}
                    style={{ margin: 0 }}
                  >
                    {account?.title}
                  </Tag>
                );
              })}
            </Flex>
          </div>
        </>
      )}

      {/* Footer - Timezone */}
      <div className="series-card-footer">
        <Flex align="center" gap={6}>
          <ClockCircleOutlined style={{ fontSize: '12px', opacity: 0.6 }} />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {TimezoneLabels[series.timezone]}
          </Text>
        </Flex>
        <Button
          type="primary"
          icon={<EyeOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            onView();
          }}
        >
          {t('series.card.view')}
        </Button>
      </div>
    </Card>
  );
};
